// Anzeige und Bedienung der Taktanalyse in der Mediathek.
//
// Eigene Datei, damit die ohnehin lange Mediathek-Seite nicht noch länger wird und
// die Zusammenführung mit anderen Zweigen einfach bleibt.

import { useCallback, useEffect, useState } from "react";
import { api, taktApi } from "../api";
import { errorText } from "./streaming-ui";
import type { LibraryItem, TaktBackfillRun } from "../types";

/** Zwölf Zahlen, je zwei Tonarten: das Camelot-Rad zur Auswahl. */
export const CAMELOT_ALLE: string[] = Array.from({ length: 12 }, (_, i) => [`${i + 1}A`, `${i + 1}B`]).flat();

/**
 * Wie sicher ist die Messung? In Worten statt in Kommazahlen – „0,63“ sagt niemandem
 * etwas, „ziemlich sicher“ schon.
 */
export function sicherheitText(c: number | null): string {
  if (c === null) return "";
  if (c >= 0.8) return "sicher";
  if (c >= 0.6) return "ziemlich sicher";
  return "eher unsicher";
}

// Je sicherer die Messung, desto kräftiger das Etikett; von Hand gesetzt in der Hausfarbe.
function sicherheitFarbe(c: number | null, manuell: boolean): string {
  if (manuell) return "border-brand-primary/40 bg-brand-primary/10 text-brand-primary";
  if (c === null) return "border-line bg-surface-sunken text-muted";
  if (c >= 0.8) return "border-line bg-surface-sunken text-secondary";
  if (c >= 0.6) return "border-line bg-surface-sunken text-muted";
  return "border-dashed border-line-strong text-muted";
}

/** Tempo und Tonart in einer Zeile der Mediathek. */
export function TaktBadge({ item }: { item: LibraryItem }): JSX.Element | null {
  if (item.bpm === null && item.musical_key_camelot === null) return null;
  const manuellBpm = item.bpm_source === "manual";
  const manuellKey = item.musical_key_source === "manual";
  return (
    <span className="inline-flex items-center gap-1">
      {item.bpm !== null && (
        <span
          className={`inline-flex items-center rounded-full border px-2 py-0 text-[0.7rem] font-medium leading-5 tabular-nums ${sicherheitFarbe(item.bpm_confidence, manuellBpm)}`}
          title={
            manuellBpm
              ? "Von Hand gesetzt. Keine Messung überschreibt diesen Wert."
              : `Gemessen, ${sicherheitText(item.bpm_confidence)}${item.beat_grid ? ` · ${item.beats_per_bar ?? 4}/4-Gitter ab ${(item.beat_grid.first_beat_ms / 1000).toFixed(2)} s` : " · ohne Beatgitter"}`
          }
        >
          {Math.round(item.bpm)} BPM{manuellBpm ? " ✎" : ""}
        </span>
      )}
      {item.musical_key_camelot && (
        <span
          className={`inline-flex items-center rounded-full border px-2 py-0 text-[0.7rem] font-medium leading-5 tabular-nums ${sicherheitFarbe(item.key_confidence, manuellKey)}`}
          title={
            manuellKey
              ? "Von Hand gesetzt. Keine Messung überschreibt diesen Wert."
              : `${item.musical_key ?? ""} · Open Key ${item.musical_key_open ?? "—"} · ${sicherheitText(item.key_confidence)}`
          }
        >
          {item.musical_key_camelot}
          {manuellKey ? " ✎" : ""}
        </span>
      )}
    </span>
  );
}

/** Filter- und Sortierleiste für Tempo und Tonart. */
export interface TaktFilterWert {
  bpm_min: string;
  bpm_max: string;
  camelot: string;
  ohne_bpm: boolean;
  sort: "neu" | "titel" | "interpret" | "bpm" | "bpm_ab" | "tonart";
}

export const TAKT_FILTER_LEER: TaktFilterWert = { bpm_min: "", bpm_max: "", camelot: "", ohne_bpm: false, sort: "neu" };

export function TaktFilter({ wert, onChange }: { wert: TaktFilterWert; onChange: (w: TaktFilterWert) => void }): JSX.Element {
  const set = (teil: Partial<TaktFilterWert>): void => onChange({ ...wert, ...teil });
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-secondary">
      <span className="section-label">Tempo</span>
      <input
        type="number"
        min={30}
        max={300}
        placeholder="von"
        value={wert.bpm_min}
        onChange={(e) => set({ bpm_min: e.target.value })}
        className="input h-8 w-20 px-2 text-xs tabular-nums"
        aria-label="Tempo von (BPM)"
      />
      <span className="text-muted">bis</span>
      <input
        type="number"
        min={30}
        max={300}
        placeholder="bis"
        value={wert.bpm_max}
        onChange={(e) => set({ bpm_max: e.target.value })}
        className="input h-8 w-20 px-2 text-xs tabular-nums"
        aria-label="Tempo bis (BPM)"
      />
      <select value={wert.camelot} onChange={(e) => set({ camelot: e.target.value })} className="h-8 rounded-lg border px-2 text-xs" aria-label="Tonart (Camelot)">
        <option value="">Tonart: alle</option>
        {CAMELOT_ALLE.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>
      <label className="flex h-8 cursor-pointer items-center gap-1.5 rounded-lg px-2 hover:bg-surface-hover" title="Zeigt nur Titel, bei denen kein Tempo gemessen werden konnte.">
        <input type="checkbox" checked={wert.ohne_bpm} onChange={(e) => set({ ohne_bpm: e.target.checked })} />
        ohne Tempo
      </label>
      <select value={wert.sort} onChange={(e) => set({ sort: e.target.value as TaktFilterWert["sort"] })} className="h-8 rounded-lg border px-2 text-xs sm:ml-auto" aria-label="Sortierung">
        <option value="neu">zuletzt hinzugefügt</option>
        <option value="titel">Titel A–Z</option>
        <option value="interpret">Interpret A–Z</option>
        <option value="bpm">Tempo aufsteigend</option>
        <option value="bpm_ab">Tempo absteigend</option>
        <option value="tonart">Tonart (Camelot)</option>
      </select>
      {(wert.bpm_min || wert.bpm_max || wert.camelot || wert.ohne_bpm || wert.sort !== "neu") && (
        <button type="button" onClick={() => onChange(TAKT_FILTER_LEER)} className="btn btn-ghost btn-sm">
          zurücksetzen
        </button>
      )}
    </div>
  );
}

/**
 * Korrektur von Hand.
 *
 * Wer hier etwas einträgt, hat hingehört. Der Wert wird als „von Hand“ vermerkt und
 * von keiner Messung mehr angefasst – auch nicht beim Nachrechnen des Bestands.
 */
export function TaktEditor({ item, onChanged }: { item: LibraryItem; onChanged: (patch: Partial<LibraryItem>) => void }): JSX.Element {
  const [bpm, setBpm] = useState(item.bpm === null ? "" : String(Math.round(item.bpm * 100) / 100));
  const [camelot, setCamelot] = useState(item.musical_key_camelot ?? "");
  const [takt, setTakt] = useState(String(item.beats_per_bar ?? 4));
  const [state, setState] = useState<"idle" | "saving" | "saved" | string>("idle");

  const speichern = async (): Promise<void> => {
    setState("saving");
    try {
      const patch = await taktApi.korrigieren(item.id, {
        bpm: bpm === "" ? null : Number(bpm),
        beats_per_bar: Number(takt),
        musical_key_camelot: camelot === "" ? null : camelot,
      });
      onChanged(patch);
      setState("saved");
    } catch (err) {
      setState(errorText(err));
    }
  };

  const neuMessen = async (): Promise<void> => {
    setState("saving");
    try {
      await taktApi.messen(item.id);
      const frisch = await api.getLibraryItem(item.id);
      setBpm(frisch.bpm === null ? "" : String(Math.round(frisch.bpm * 100) / 100));
      setCamelot(frisch.musical_key_camelot ?? "");
      setTakt(String(frisch.beats_per_bar ?? 4));
      onChanged(frisch);
      setState("saved");
    } catch (err) {
      setState(errorText(err));
    }
  };

  return (
    <div className="well">
      <div className="section-label mb-2">
        Takt und Tonart
        {item.bpm_source === "manual" || item.musical_key_source === "manual" ? <span className="ml-1 normal-case tracking-normal text-brand-primary">· von Hand gesetzt</span> : null}
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="field-label">Tempo (BPM)</span>
          <input
            type="number"
            min={30}
            max={300}
            step={0.01}
            value={bpm}
            onChange={(e) => {
              setBpm(e.target.value);
              setState("idle");
            }}
            className="input w-24 tabular-nums"
          />
        </label>
        <label className="block">
          <span className="field-label">Schläge je Takt</span>
          <select
            value={takt}
            onChange={(e) => {
              setTakt(e.target.value);
              setState("idle");
            }}
            className="input w-20"
          >
            {[2, 3, 4, 5, 6, 7].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="field-label">Tonart (Camelot)</span>
          <select
            value={camelot}
            onChange={(e) => {
              setCamelot(e.target.value);
              setState("idle");
            }}
            className="input w-24"
          >
            <option value="">—</option>
            {CAMELOT_ALLE.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <button type="button" onClick={() => void speichern()} disabled={state === "saving"} className="btn btn-secondary">
          Von Hand festlegen
        </button>
        <button type="button" onClick={() => void neuMessen()} disabled={state === "saving"} className="btn btn-ghost">
          Nur Takt neu messen
        </button>
        {state === "saved" && <span className="badge badge-success">Gespeichert.</span>}
        {state !== "idle" && state !== "saving" && state !== "saved" && <span className="text-xs text-red-600">{state}</span>}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-3 text-xs text-muted">
        {item.bpm !== null && (
          <span>
            Tempo: {item.bpm_source === "manual" ? "von Hand gesetzt" : `gemessen, ${sicherheitText(item.bpm_confidence)} (${item.bpm_confidence?.toLocaleString("de-DE") ?? "—"})`}
          </span>
        )}
        {item.musical_key_camelot && (
          <span>
            Tonart: {item.musical_key ?? item.musical_key_camelot} ·{" "}
            {item.musical_key_source === "manual" ? "von Hand gesetzt" : `gemessen, ${sicherheitText(item.key_confidence)} (${item.key_confidence?.toLocaleString("de-DE") ?? "—"})`}
          </span>
        )}
      </div>
      <div className="mt-1 text-xs text-muted">
        {item.bpm === null && item.takt_version !== null
          ? "Gemessen, aber kein sicheres Ergebnis – bei Sprache, Klassik und freiem Tempo ist das normal."
          : item.beat_grid
            ? `Erster Taktanfang bei ${(item.beat_grid.first_beat_ms / 1000).toFixed(2)} s${item.beat_grid.segments ? `, Tempo wandert (${item.beat_grid.segments.length} Abschnitte)` : ""}.`
            : item.bpm !== null
              ? "Tempo gemessen, für ein Beatgitter aber zu unsicher."
              : "Noch nicht gemessen."}
        {item.tuning_cents ? ` Stimmung ${item.tuning_cents > 0 ? "+" : ""}${item.tuning_cents} Cent.` : ""}
      </div>
    </div>
  );
}

/**
 * Nachrechnen des Bestands: Fortschritt, Start und Abbruch.
 *
 * Läuft schonend im Hintergrund – ein Titel, dann Pause. Bei tausend Titeln dauert
 * das eine Nacht, und genau so ist es gedacht: Die laufende Sendung geht vor.
 */
export function NachrechnenPanel({ onFertig }: { onFertig?: () => void }): JSX.Element | null {
  const [run, setRun] = useState<TaktBackfillRun | null>(null);
  const [offen, setOffen] = useState(0);
  const [fehler, setFehler] = useState<string | null>(null);

  const laden = useCallback(async (): Promise<void> => {
    try {
      const stand = await taktApi.nachrechnenStand();
      setRun(stand.run);
      setOffen(stand.offen);
      setFehler(null);
    } catch (err) {
      setFehler(errorText(err));
    }
  }, []);

  useEffect(() => {
    void laden();
  }, [laden]);

  const laeuft = run?.status === "running";
  useEffect(() => {
    if (!laeuft) return;
    const t = setInterval(() => {
      void laden().then(() => onFertig?.());
    }, 3000);
    return () => clearInterval(t);
  }, [laeuft, laden, onFertig]);

  if (fehler) return <div className="callout callout-danger mb-4 text-xs">{fehler}</div>;
  if (!laeuft && offen === 0) return null;

  const anteil = run && run.total > 0 ? Math.round((run.done / run.total) * 100) : 0;
  return (
    <div className="card mb-4 p-4 text-sm">
      {laeuft && run ? (
        <>
          <div className="mb-1 flex flex-wrap items-center gap-2">
            <span className="font-medium text-primary">Takt und Tonart werden nachgerechnet …</span>
            <span className="text-xs tabular-nums text-muted">
              {run.done} von {run.total}
              {run.skipped > 0 ? ` · ${run.skipped} ohne sicheres Ergebnis` : ""}
              {run.failed > 0 ? ` · ${run.failed} nicht lesbar` : ""}
            </span>
            <button type="button" onClick={() => void taktApi.nachrechnenAbbrechen().then(laden)} className="btn btn-secondary btn-sm ml-auto">
              Abbrechen
            </button>
          </div>
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-input" role="meter" aria-valuenow={anteil} aria-valuemin={0} aria-valuemax={100} aria-label="Fortschritt der Nachrechnung">
            <div className="h-full rounded-full bg-brand-primary transition-all" style={{ width: `${anteil}%` }} />
          </div>
          {run.current_title && <div className="mt-1 truncate text-xs text-muted">gerade: {run.current_title}</div>}
          <div className="mt-1 text-xs text-muted">Läuft schonend im Hintergrund – ein Titel, dann Pause. Die laufende Sendung wird dadurch nicht langsamer.</div>
        </>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-brand-primary/10 text-xs font-semibold tabular-nums text-brand-primary">{offen > 99 ? "99+" : offen}</span>
          <span className="text-secondary">
            {offen} {offen === 1 ? "Titel hat" : "Titel haben"} noch kein gemessenes Tempo.
          </span>
          <button type="button" onClick={() => void taktApi.nachrechnenStarten().then(laden)} className="btn btn-secondary btn-sm">
            Bestand nachrechnen
          </button>
          {run && run.status !== "running" && (
            <span className="text-xs text-muted">
              Letzter Lauf: {run.status === "done" ? "fertig" : run.status === "cancelled" ? "abgebrochen" : "fehlgeschlagen"} nach {run.done} von {run.total}.
            </span>
          )}
        </div>
      )}
    </div>
  );
}
